import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Search, FileText, Mail, Users, BookOpen, Send, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminResourceDownloads() {
  const [data, setData] = useState({ items: [], total: 0, unique_email_count: 0, by_resource: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [blastOpen, setBlastOpen] = useState(false);
  const [blast, setBlast] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const sendBlast = async () => {
    if (!blast.subject.trim() || !blast.message.trim()) return toast.error('Subject and message are required');
    setSending(true);
    try {
      const payload = { subject: blast.subject, message: blast.message };
      if (resourceFilter) payload.resource_id = resourceFilter;
      const r = await axios.post(`${API_URL}/api/admin/resource-downloads/email-blast`, payload, { withCredentials: true });
      toast.success(`Email queued to ${r.data.recipients} downloader${r.data.recipients === 1 ? '' : 's'}`);
      setBlastOpen(false);
      setBlast({ subject: '', message: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send blast');
    } finally {
      setSending(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm.trim()) params.q = searchTerm.trim();
      if (resourceFilter) params.resource_id = resourceFilter;
      const res = await axios.get(`${API_URL}/api/admin/resource-downloads`, {
        withCredentials: true,
        params,
      });
      setData(res.data || { items: [], total: 0, unique_email_count: 0, by_resource: [] });
    } catch (e) {
      console.error('Error fetching resource downloads:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceFilter]);

  // Debounce search input -> server query
  useEffect(() => {
    const t = setTimeout(fetchData, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const resourceOptions = useMemo(() => {
    const seen = new Map();
    (data.by_resource || []).forEach((r) => {
      if (!seen.has(r.resource_name)) seen.set(r.resource_name, r.resource_name);
    });
    return Array.from(seen.values());
  }, [data.by_resource]);

  const handleExport = () => {
    window.open(`${API_URL}/api/admin/resource-downloads/export.csv`, '_blank');
  };

  return (
    <AdminLayout title="Resource Downloads">
      <div data-testid="admin-resource-downloads-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="resource-downloads-title">
            Resource Downloads ({data.items.length})
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBlastOpen(true)}
              className="inline-flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#EA580C] transition-colors"
              data-testid="email-list-btn"
            >
              <Send size={16} />
              Email my list
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Email blast modal */}
        {blastOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-blast-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-[#0A0F1C]">Email my list</h2>
                <button onClick={() => setBlastOpen(false)} data-testid="blast-close"><X size={18} /></button>
              </div>
              <p className="text-sm text-[#475569] mb-4">
                Sends to {resourceFilter ? 'downloaders of the selected resource' : `all ${data.unique_email_count} unique downloaders`}. A branded header and one-click unsubscribe are added automatically.
              </p>
              <input
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-2"
                placeholder="Subject line"
                value={blast.subject}
                onChange={(e) => setBlast({ ...blast, subject: e.target.value })}
                data-testid="blast-subject"
              />
              <textarea
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-3"
                rows={6}
                placeholder="Your message. Plain text or HTML. e.g. 'We just updated the Business Jumpstart Checklist — grab the new version here: <link>'"
                value={blast.message}
                onChange={(e) => setBlast({ ...blast, message: e.target.value })}
                data-testid="blast-message"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setBlastOpen(false)} className="px-4 py-2 text-sm text-[#475569]" data-testid="blast-cancel">Cancel</button>
                <button onClick={sendBlast} disabled={sending} className="inline-flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60" data-testid="blast-send">
                  <Send size={14} />{sending ? 'Sending…' : 'Send to list'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            icon={<FileText size={20} />}
            label="Total Downloads"
            value={data.total}
            tone="blue"
            testId="stat-total"
          />
          <StatCard
            icon={<Users size={20} />}
            label="Unique Emails"
            value={data.unique_email_count}
            tone="green"
            testId="stat-unique"
          />
          <StatCard
            icon={<BookOpen size={20} />}
            label="Resources"
            value={(data.by_resource || []).length}
            tone="orange"
            testId="stat-resources"
          />
        </div>

        {/* By Resource breakdown */}
        {(data.by_resource || []).length > 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 mb-6">
            <h2 className="text-sm font-bold text-[#0A0F1C] mb-3 uppercase tracking-wider">
              Downloads by Resource
            </h2>
            <div className="space-y-2" data-testid="by-resource-list">
              {data.by_resource.map((r) => (
                <div
                  key={r.resource_name}
                  className="flex items-center justify-between border-b border-[#F1F5F9] py-2 last:border-0"
                >
                  <div className="text-sm font-medium text-[#0A0F1C] flex-1 truncate pr-3">
                    {r.resource_name}
                  </div>
                  <div className="text-xs text-[#475569] flex items-center gap-3">
                    <span>{r.count} downloads</span>
                    <span className="text-[#94A3B8]">|</span>
                    <span>{r.unique_count} unique</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 border border-[#E2E8F0] rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-grow relative">
              <Input
                type="text"
                placeholder="Search by name, email, or resource..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-testid="downloads-search"
              />
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            </div>
            <select
              value={resourceFilter}
              onChange={(e) => setResourceFilter(e.target.value)}
              className="form-input w-full md:w-64"
              data-testid="downloads-resource-filter"
            >
              <option value="">All Resources</option>
              {resourceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : data.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="downloads-table">
                <thead className="bg-[#F9FAFB]">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">
                      Resource
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-[#475569] uppercase tracking-wider">
                      Downloaded
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.items.map((row, index) => (
                    <tr key={row.download_id || index} className="hover:bg-[#F9FAFB]" data-testid={`download-row-${index}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#0A0F1C]">{row.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={`mailto:${row.email}`}
                          className="text-sm text-[#1D4ED8] flex items-center gap-1 hover:underline"
                        >
                          <Mail size={14} />
                          {row.email}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#475569]">{row.resource_name || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#475569]">
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center" data-testid="downloads-empty">
              <FileText size={48} className="text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-[#475569]">No resource downloads yet</p>
              <p className="text-xs text-[#94A3B8] mt-1">
                Captured leads from your free resources will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, tone, testId }) {
  const tones = {
    blue: 'bg-[#EFF6FF] text-[#1D4ED8]',
    green: 'bg-green-50 text-green-700',
    orange: 'bg-[#FFF7ED] text-[#F97316]',
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone] || tones.blue}`}>
          {icon}
        </div>
        <div>
          <div className="text-xs text-[#475569] uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold text-[#0A0F1C]">{value}</div>
        </div>
      </div>
    </div>
  );
}
